/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/EventInvitationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Select from "react-select";
import styles from "./index.module.css";
import { getCompanyConnectionRequests } from "../../logic/connection";

const EventInvitationModal = ({
  dialogRef = null,
  open,
  onClose,
  onConfirm,
  isLoading = false,
  events = [],
  senderCompanyRFC = null,
  recipientCompanyRFC = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);

  const fetchConnectionRequests = async () => {
    if (!senderCompanyRFC || !recipientCompanyRFC) {
      return;
    }

    setIsCheckingConnection(true);
    try {
      const data = await getCompanyConnectionRequests(
        senderCompanyRFC,
        recipientCompanyRFC,
        navigate,
      );
      setConnectionRequests(data.connectionRequests || []);
    } catch (error) {
      logger.error("Error fetching connection requests:", {
        err: sanitizeError(error),
      });
      setConnectionRequests([]);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  useEffect(() => {
    if (dialogRef && dialogRef.current) {
      const dialog = dialogRef.current;
      if (open) {
        dialog.showModal();
        // Reset selections when modal opens
        setSelectedEvent(null);
        // Fetch connection requests when modal opens
        if (senderCompanyRFC && recipientCompanyRFC) {
          fetchConnectionRequests();
        }
      } else {
        dialog.close();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dialogRef, senderCompanyRFC, recipientCompanyRFC]);

  useEffect(() => {
    // Cleanup: close dialog when component unmounts
    return () => {
      if (dialogRef && dialogRef.current) {
        dialogRef.current.close();
      }
    };
  }, [dialogRef]);

  const handleConfirm = async () => {
    if (!selectedEvent) {
      return; // Don't proceed if event not selected
    }

    if (onConfirm) {
      await onConfirm(selectedEvent);
      // Modal will be closed by parent after successful operation
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Check if there's already a connection request for the selected event
  const hasExistingRequest = () => {
    if (!selectedEvent || !selectedEvent.value) {
      return false;
    }

    const selectedEventId =
      selectedEvent.value.event?._id || selectedEvent.value._id;
    if (!selectedEventId) {
      return false;
    }

    return connectionRequests.some(
      (request) => request.businessEventId === selectedEventId,
    );
  };

  const isConfirmDisabled =
    !selectedEvent || isLoading || isCheckingConnection || hasExistingRequest();

  // Transform events for the select dropdown
  const eventOptions = events.map((eventData) => ({
    value: eventData,
    label: eventData.event?.nombre || t("common.unnamedEvent"),
  }));

  return (
    <>
      {open && <div className={styles.modalOverlay}></div>}
      <dialog ref={dialogRef} className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {t("pages.companyProfile.eventInvitationModalTitle")}
          </h3>
          <button
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.modalSubtitle}>
            {t("pages.companyProfile.eventInvitationModalSubtitle")}
          </p>

          <div className={styles.formSection}>
            <label className={styles.formLabel}>
              {t("pages.companyProfile.eventInvitationEventLabel")}
            </label>
            <Select
              options={eventOptions}
              value={selectedEvent}
              onChange={setSelectedEvent}
              placeholder={t(
                "pages.companyProfile.eventInvitationEventPlaceholder",
              )}
              isDisabled={isLoading}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderRadius: "8px",
                  width: "100%",
                  height: "40px",
                  fontSize: "16px",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  borderColor: baseStyles.borderColor,
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                }),
                valueContainer: (baseStyles) => ({
                  ...baseStyles,
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                }),
                option: (baseStyles) => ({
                  ...baseStyles,
                  cursor: "pointer",
                }),
                indicatorSeparator: () => ({ display: "none" }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 10002,
                }),
              }}
            />
          </div>
          {hasExistingRequest() && (
            <p
              style={{
                color: "#d32f2f",
                fontSize: "14px",
                marginTop: "10px",
                textAlign: "center",
              }}
            >
              {t("pages.companyProfile.eventInvitationAlreadySent") ||
                "Ya se ha enviado una invitación para este evento en los últimos 30 días."}
            </p>
          )}
        </div>

        <div className={styles.modalActions}>
          <button
            className={`${styles.modalButton} ${styles.cancelButton}`}
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </button>
          <button
            className={`${styles.modalButton} ${styles.confirmButton}`}
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {isLoading ? (
              <span className={styles.loadingText}>
                {t("pages.companyProfile.sendingEventInvitation")}
              </span>
            ) : (
              t("pages.companyProfile.sendEventInvitation")
            )}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default EventInvitationModal;
